import { empresaAtiva } from '../pages/permissoes/situacaoEmpresa';
import { api } from '../services/api';
import { IData, IDataNoPagination } from '../types/shared';
import { ITela } from './useTelas';

// Rotas que a Central de Permissões consome. Todas exigem admin no backend.

export interface ITelaCatalogo extends ITela {
  grupos: number[];
}

export interface IGrupoResumo {
  id: number;
  nome: string;
  usuarios_ativos: number;
  telas_liberadas: number;
  ve_todas_carteiras: boolean;
  usuarios_adicionais: number;
}

export interface IMembroGrupo {
  id: number;
  nome: string | null;
  email: string;
  ativo: boolean;
  papel: 'principal' | 'adicional';
}

export interface IGruposDoUsuario {
  grupo_principal_id: number;
  adicionais: number[];
  /** O que principal + adicionais dão juntos (sem as exceções do usuário). */
  permissoes_dos_grupos: number[];
  carteira_dos_grupos: string[];
  /** Empresas avulsas que principal + adicionais liberam. */
  empresas_dos_grupos?: IEmpresaCarteira[];
}

export interface IGrupoDeEmpresasResumo {
  nome: string;
  total_empresas: number;
}

/** Empresa liberada uma a uma na carteira (soma aos grupos de empresas). */
export interface IEmpresaCarteira {
  id: number;
  nome: string;
  cnpj: string | null;
  grupo_de_empresas: string | null;
  /** Código da empresa no sistema de origem (na tela: "ID"). */
  external_id?: string | null;
  /** Situação (regra do Orquestrador em `pages/permissoes/situacaoEmpresa.ts`). */
  ativa?: string | null;
  dt_cli_desde?: string | null;
  dt_cli_ate?: string | null;
}

/** Busca de empresa: nome com 2+ letras, ou ID/CNPJ com qualquer quantidade de números. */
export const podeBuscarEmpresa = (texto: string) => {
  const t = texto.trim();
  return t.length >= 2 || /^\d+$/.test(t);
};

/** "12.345.678/0001-90 · ID 1136" */
export const identificacaoDaEmpresa = (e: Pick<IEmpresaCarteira, 'cnpj' | 'external_id'>) => {
  const d = (e.cnpj ?? '').replace(/\D/g, '');
  const cnpj =
    d.length === 14
      ? `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`
      : (e.cnpj ?? '');
  return [cnpj, e.external_id ? `ID ${e.external_id}` : ''].filter(Boolean).join(' · ');
};

export interface IPermissaoGeral {
  id: number;
  codigo: string;
  nome: string;
}

export interface ITelaDoGrupo {
  tela_id: number;
  tela_codigo: string;
  tela_nome: string;
  tela_path: string;
  acoes: string[];
}

export interface IUsuarioLista {
  id: number;
  nome: string | null;
  email: string;
  grupo_id: number;
  ativo: boolean;
}

export interface IExcecoesUsuario {
  especificas: IPermissaoGeral[];
  desativadas: IPermissaoGeral[];
}

export interface IColunaKanbanPermissao {
  grupo_id: number;
  coluna_titulo: string;
  acao: string;
  permitido: boolean;
}

export interface IDeptConfigWhatsapp {
  restrict_by_dept: boolean;
  blur_messages: boolean;
  extra_depts: string[];
}

const dados = <T>(res: { data: IDataNoPagination<T> }) => res.data.data;

export const centralApi = {
  catalogo: async () =>
    dados(await api.get<IDataNoPagination<ITelaCatalogo[]>>('/api/v1/telas/catalogo/')),

  gruposResumo: async () =>
    dados(await api.get<IDataNoPagination<IGrupoResumo[]>>('/api/v1/grupos/resumo/')),

  permissoesGerais: async () =>
    dados(await api.get<IDataNoPagination<IPermissaoGeral[]>>('/api/v1/permissoes/gerais/')),

  telasDoGrupo: async (grupoId: number) =>
    dados(await api.get<IDataNoPagination<ITelaDoGrupo[]>>(`/api/v1/grupos/${grupoId}/telas/`)),

  salvarTelasDoGrupo: async (grupoId: number, permissoes: { tela_id: number; acoes: string[] }[]) =>
    dados(await api.put(`/api/v1/grupos/${grupoId}/telas/`, { permissoes })),

  geraisDoGrupo: async (grupoId: number) =>
    dados(
      await api.get<IDataNoPagination<IPermissaoGeral[]>>(
        `/api/v1/grupos/${grupoId}/permissoes-admin/`,
      ),
    ),

  salvarGeraisDoGrupo: async (grupoId: number, permissoesIds: number[]) =>
    dados(
      await api.put(`/api/v1/grupos/${grupoId}/permissoes-admin/`, {
        permissoes_ids: permissoesIds,
      }),
    ),

  carteirasDisponiveis: async () =>
    dados(
      await api.get<IDataNoPagination<string[]>>('/api/v1/grupos/grupos-empresas/disponiveis/'),
    ),

  carteiraDoGrupo: async (grupoId: number) =>
    dados(await api.get<IDataNoPagination<string[]>>(`/api/v1/grupos/${grupoId}/grupos-empresas/`)),

  salvarCarteiraDoGrupo: async (grupoId: number, gruposEmpresas: string[]) =>
    dados(
      await api.put(`/api/v1/grupos/${grupoId}/grupos-empresas/`, {
        grupos_empresas: gruposEmpresas,
      }),
    ),

  usuario: async (usuarioId: number): Promise<IUsuarioLista> =>
    dados(await api.get<IDataNoPagination<IUsuarioLista>>(`/api/v1/usuarios/${usuarioId}/`)),

  /** `ativo` undefined = ativos e inativos. */
  usuarios: async (busca: string, ativo: boolean | undefined = true) => {
    const res = await api.get<IData<IUsuarioLista[]>>('/api/v1/usuarios/', {
      params: { page: 1, size: 100, search: busca || undefined, ativo, excluir_grupo_id: 7 },
    });
    return res.data.data.items;
  },
  totalDeUsuarios: async (ativo?: boolean) => {
    const res = await api.get<IData<IUsuarioLista[]>>('/api/v1/usuarios/', {
      params: { page: 1, size: 1, ativo, excluir_grupo_id: 7 },
    });
    return res.data.data.total ?? 0;
  },

  excecoesDoUsuario: async (usuarioId: number) =>
    dados(
      await api.get<IDataNoPagination<IExcecoesUsuario>>(
        `/api/v1/usuarios/${usuarioId}/permissoes/`,
      ),
    ),

  salvarExcecoesDoUsuario: async (
    usuarioId: number,
    especificas: number[],
    desativadas: number[],
  ) =>
    dados(await api.put(`/api/v1/usuarios/${usuarioId}/permissoes/`, { especificas, desativadas })),

  carteiraDoUsuario: async (usuarioId: number) =>
    dados(
      await api.get<IDataNoPagination<string[]>>(`/api/v1/usuarios/${usuarioId}/grupos-empresas/`),
    ),

  salvarCarteiraDoUsuario: async (usuarioId: number, gruposEmpresas: string[]) =>
    dados(
      await api.put(`/api/v1/usuarios/${usuarioId}/grupos-empresas/`, {
        grupos_empresas: gruposEmpresas,
      }),
    ),

  colunasKanban: async (grupoId: number) =>
    dados(
      await api.get<IDataNoPagination<IColunaKanbanPermissao[]>>(
        '/api/v1/kanban/evento-coluna-permissoes/',
        { params: { grupo_id: grupoId } },
      ),
    ),

  salvarColunasKanban: async (itens: IColunaKanbanPermissao[]) =>
    api.put('/api/v1/kanban/evento-coluna-permissoes/', itens),

  membrosDoGrupo: async (grupoId: number) =>
    dados(await api.get<IDataNoPagination<IMembroGrupo[]>>(`/api/v1/grupos/${grupoId}/membros/`)),

  adicionarMembro: async (grupoId: number, usuarioId: number, como: 'adicional' | 'principal') =>
    dados(
      await api.post<IDataNoPagination<IMembroGrupo[]>>(`/api/v1/grupos/${grupoId}/membros/`, {
        usuario_id: usuarioId,
        como,
      }),
    ),

  removerMembro: async (grupoId: number, usuarioId: number) =>
    dados(
      await api.delete<IDataNoPagination<IMembroGrupo[]>>(
        `/api/v1/grupos/${grupoId}/membros/${usuarioId}/`,
      ),
    ),

  gruposDoUsuario: async (usuarioId: number) =>
    dados(
      await api.get<IDataNoPagination<IGruposDoUsuario>>(`/api/v1/usuarios/${usuarioId}/grupos/`),
    ),

  salvarGruposDoUsuario: async (usuarioId: number, principal: number, adicionais: number[]) =>
    dados(
      await api.put<IDataNoPagination<IGruposDoUsuario>>(`/api/v1/usuarios/${usuarioId}/grupos/`, {
        grupo_principal_id: principal,
        adicionais,
      }),
    ),

  criarGrupo: async (nome: string) =>
    dados(
      await api.post<IDataNoPagination<{ id: number; nome: string }>>('/api/v1/grupos/', { nome }),
    ),

  /** Busca por nome ou, se vier número, por CNPJ (a lista de empresas já existente). */
  /** Só números: ID exato (external_id) e, com 4+ dígitos, CNPJ. Com letras: nome. */
  /** Só traz empresas ATIVAS: inativa só se vê (e reativa) na aba Empresas. */
  buscarEmpresas: async (texto: string): Promise<IEmpresaCarteira[]> => {
    const t = texto.trim();
    const soNumeros = /^[\d./-]+$/.test(t);
    const digitos = t.replace(/\D/g, '');
    const filtros: Record<string, string>[] = [];
    if (soNumeros && digitos) {
      filtros.push({ external_id: digitos });
      if (digitos.length >= 4) filtros.push({ cnpj: digitos });
    } else filtros.push({ nome: t });
    const respostas = await Promise.all(
      filtros.map(f =>
        api.get<IData<IEmpresaCarteira[]>>('/api/v1/empresas/', {
          params: { page: 1, size: 20, ...f },
        }),
      ),
    );
    const porId = new Map<number, IEmpresaCarteira>();
    respostas.forEach(r =>
      r.data.data.items.forEach(e => {
        if (!porId.has(e.id))
          porId.set(e.id, {
            id: e.id,
            nome: e.nome,
            cnpj: e.cnpj,
            grupo_de_empresas: e.grupo_de_empresas,
            external_id: e.external_id,
            ativa: e.ativa,
            dt_cli_desde: e.dt_cli_desde,
            dt_cli_ate: e.dt_cli_ate,
          });
      }),
    );
    return Array.from(porId.values()).filter(empresaAtiva);
  },

  empresasDoGrupo: async (grupoId: number) =>
    dados(
      await api.get<IDataNoPagination<IEmpresaCarteira[]>>(
        `/api/v1/grupos/${grupoId}/carteira/empresas/`,
      ),
    ),

  salvarEmpresasDoGrupo: async (grupoId: number, empresasIds: number[]) =>
    dados(
      await api.put<IDataNoPagination<IEmpresaCarteira[]>>(
        `/api/v1/grupos/${grupoId}/carteira/empresas/`,
        { empresas_ids: empresasIds },
      ),
    ),

  empresasDoUsuario: async (usuarioId: number) =>
    dados(
      await api.get<IDataNoPagination<IEmpresaCarteira[]>>(
        `/api/v1/usuarios/${usuarioId}/carteira/empresas/`,
      ),
    ),

  /** Grupos de empresas com quantas empresas cada um tem. */
  resumoGruposDeEmpresas: async () =>
    dados(
      await api.get<IDataNoPagination<IGrupoDeEmpresasResumo[]>>('/api/v1/grupos-empresas/resumo/'),
    ),

  /** Empresas de um grupo de empresas (nome exato), todas as páginas. */
  empresasDoGrupoDeEmpresas: async (nome: string): Promise<IEmpresaCarteira[]> => {
    const todas: IEmpresaCarteira[] = [];
    for (let page = 1; page <= 20; page += 1) {
      // eslint-disable-next-line no-await-in-loop
      const res = await api.get<IData<IEmpresaCarteira[]>>('/api/v1/empresas/', {
        params: { page, size: 100, grupo_de_empresas: nome },
      });
      const { items, pages } = res.data.data;
      items
        .filter(e => (e.grupo_de_empresas ?? '').trim() === nome)
        .forEach(e =>
          todas.push({
            id: e.id,
            nome: e.nome,
            cnpj: e.cnpj,
            grupo_de_empresas: e.grupo_de_empresas,
            external_id: e.external_id,
            ativa: e.ativa,
            dt_cli_desde: e.dt_cli_desde,
            dt_cli_ate: e.dt_cli_ate,
          }),
        );
      if (page >= pages) break;
    }
    return todas;
  },

  /** Define as empresas do grupo de empresas (cria o grupo se o nome for novo). */
  salvarGrupoDeEmpresas: async (nome: string, empresasIds: number[]) =>
    dados(
      await api.put<IDataNoPagination<IEmpresaCarteira[]>>('/api/v1/grupos-empresas/empresas/', {
        nome,
        empresas_ids: empresasIds,
      }),
    ),

  /** Põe UMA empresa num grupo de empresas (null = tira de qualquer grupo). */
  alocarEmpresa: async (empresaId: number, grupoDeEmpresas: string | null) =>
    dados(
      await api.put<IDataNoPagination<IEmpresaCarteira>>('/api/v1/grupos-empresas/alocar/', {
        empresa_id: empresaId,
        grupo_de_empresas: grupoDeEmpresas,
      }),
    ),

  /** Renomeia o grupo de empresas e as carteiras que o usam (grupos e usuários). */
  renomearGrupoDeEmpresas: async (nome: string, novoNome: string) =>
    dados(await api.put('/api/v1/grupos-empresas/renomear/', { nome, novo_nome: novoNome })),

  salvarEmpresasDoUsuario: async (usuarioId: number, empresasIds: number[]) =>
    dados(
      await api.put<IDataNoPagination<IEmpresaCarteira[]>>(
        `/api/v1/usuarios/${usuarioId}/carteira/empresas/`,
        { empresas_ids: empresasIds },
      ),
    ),
};
